import React, { useMemo, useState } from "react";
import { Button, Checkbox, Drawer, Empty, Input, Spin } from "antd";
import Icon from "@mdi/react";
import { mdiCheckCircle, mdiChevronDown, mdiChevronUp, mdiInformationOutline, mdiMagnify, mdiViewGridOutline, mdiViewListOutline } from "@mdi/js";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { Agent, agentLocation } from "./bookingUtils";
import PartnerDetails from "./PartnerDetails";
import SignInForm from "../../SignInForm";
import { ChangeAgentConfirm, ReasonsDialog } from "./ChangeAgentDialogs";

interface Props {
  agents: Agent[];
  loading: boolean;
  selected: Agent | null;
  stickyAgent: Agent | null;
  referralAgent: Agent | null;
  signedIn: boolean;
  reasons: { value: string; label: string }[];
  stickySubmitting: boolean;
  onSelect: (agent: Agent) => void;
  /** "Change Agent?" confirmed on the referral partner: drop the referral so another partner can be picked. */
  onReleaseReferral: () => void;
  onRequestStickyChange: (reason: string, other: string) => Promise<boolean>;
  onBackToDetails: () => void;
}

type Dialog = { kind: "confirm"; target: Agent | null } | { kind: "signin" } | { kind: "reasons" } | null;

const partnerName = (agent: Agent) => (agent.agency_full_name || agent.name || "").trim();
const partnerAddress = (agent: Agent) => [agent.address, agent.city, agent.state].filter(Boolean).join(", ");

function Logo({ agent }: { agent: Agent }) {
  return agent.image ? <img src={agent.image} alt="" /> : <b>{partnerName(agent).slice(0, 1)}</b>;
}

// Preferred Partner Selection (Figma 8821:24385 desktop list, 8821:24200 grid, 10308:317528 mobile).
export default function PartnerStep(props: Props) {
  const { agents, loading, selected, stickyAgent, referralAgent } = props;
  const isMobile = useBreakpoint();
  const [query, setQuery] = useState("");
  const [locations, setLocations] = useState<string[]>([]);
  const [view, setView] = useState<"grid" | "list">("list");
  const [chipsOpen, setChipsOpen] = useState(false);
  const [details, setDetails] = useState<Agent | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);

  const locationCounts = useMemo(() => {
    const counts = new Map<string, number>();
    agents.forEach((agent) => counts.set(agentLocation(agent), (counts.get(agentLocation(agent)) || 0) + 1));
    // Klang Valley leads, as in Series; the rest alphabetical.
    return Array.from(counts.entries()).sort((a, b) => (a[0] === "Klang Valley" ? -1 : b[0] === "Klang Valley" ? 1 : a[0].localeCompare(b[0])));
  }, [agents]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return agents.filter((agent) => {
      if (locations.length && !locations.includes(agentLocation(agent))) return false;
      if (!needle) return true;
      return [partnerName(agent), partnerAddress(agent), agent.zone].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [agents, query, locations]);

  const sameAgent = (a: Agent | null, b: Agent | null) => !!a && !!b && String(a.id) === String(b.id);
  // The Trip Advisor who owns a referral link or sticky binding is only on that object, so attach it when opening details.
  const openDetails = (agent: Agent) => {
    const owner = [referralAgent, stickyAgent].find((candidate) => sameAgent(candidate, agent));
    setDetails(owner?.subagent ? { ...agent, subagent: owner.subagent } : agent);
  };
  const toggleLocation = (location: string) => setLocations((current) => current.includes(location) ? current.filter((item) => item !== location) : [...current, location]);

  // Choosing a partner other than the preselected one asks first: a sticky partner needs sign-in and a reason,
  // a referral partner needs a confirmation (follows the Series flow).
  const requestSelect = (agent: Agent) => {
    if (stickyAgent && !sameAgent(agent, stickyAgent)) {
      setDialog(props.signedIn ? { kind: "reasons" } : { kind: "signin" });
      return;
    }
    if (referralAgent && sameAgent(selected, referralAgent) && !sameAgent(agent, referralAgent)) {
      setDialog({ kind: "confirm", target: agent });
      return;
    }
    if (sameAgent(agent, selected)) {
      // Tapping the referral partner's own "Selected" offers to change it.
      if (referralAgent && sameAgent(agent, referralAgent) && !stickyAgent) setDialog({ kind: "confirm", target: null });
      return;
    }
    props.onSelect(agent);
  };

  const confirmChange = () => {
    if (dialog?.kind === "confirm") {
      if (dialog.target) props.onSelect(dialog.target);
      else props.onReleaseReferral();
    }
    setDialog(null);
  };

  const dialogs = (
    <>
      <PartnerDetails agent={details} onClose={() => setDetails(null)} />
      <ChangeAgentConfirm
        open={dialog?.kind === "confirm"}
        picking={dialog?.kind === "confirm" && !!dialog.target}
        onOk={confirmChange}
        onCancel={() => setDialog(null)}
      />
      {/* Same as the Series booking form: the sign-in slides up over the booking, which stays where it is. */}
      <Drawer
        open={dialog?.kind === "signin"}
        onClose={() => setDialog(null)}
        placement="bottom"
        height="100%"
        closable={false}
        destroyOnClose
        className="signin-drawer"
        rootClassName="v3"
      >
        <SignInForm showAuthLinkOption onClose={() => setDialog(null)} onSuccess={() => setDialog({ kind: "reasons" })} />
      </Drawer>
      <ReasonsDialog
        open={dialog?.kind === "reasons"}
        reasons={props.reasons}
        submitting={props.stickySubmitting}
        onCancel={() => setDialog(null)}
        onSubmit={async (reason, other) => { if (await props.onRequestStickyChange(reason, other)) setDialog(null); }}
      />
    </>
  );

  const askStickyChange = () => setDialog(props.signedIn ? { kind: "reasons" } : { kind: "signin" });

  const notice = stickyAgent ? (
    <div className="gt-banner is-info is-sticky">
      <span>You’re previously assigned to {partnerName(stickyAgent)}, so this booking will be served by them.</span>
      <button type="button" className="gt-banner__link" onClick={askStickyChange}>Change Agent</button>
    </div>
  ) : referralAgent ? (
    <div className="gt-banner is-info">The partner from your referral link has been selected. You may choose another partner after confirmation.</div>
  ) : null;

  const empty = (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No preferred partners are available right now.">
      <Button onClick={props.onBackToDetails}>Back to traveller details</Button>
    </Empty>
  );

  // A customer already bound to a partner (sticky) sees only that partner, with the notice and its Change Agent link in the
  // first view; Change Agent asks for sign-in (or the reasons once signed in) and nothing else can be picked.
  if (stickyAgent) {
    return isMobile ? (
      <>
        <h2 className="gt-m-title">Your assigned partner</h2>
        {notice}
        <div className="gt-m-partners">
          <div className="gt-m-partner is-selected is-sticky">
            <div className="gt-m-partner__main">
              <Icon path={mdiCheckCircle} size="28px" color="#dc1a22" />
              <span className="gt-m-partner__logo"><Logo agent={stickyAgent} /></span>
              <span className="gt-m-partner__text">
                <strong>{partnerName(stickyAgent)}</strong>
                <small>{partnerAddress(stickyAgent)}</small>
              </span>
            </div>
            <button type="button" className="gt-m-partner__info" onClick={() => openDetails(stickyAgent)} aria-label="Partner details">
              <Icon path={mdiInformationOutline} size="26px" color="#1890ff" />
            </button>
          </div>
        </div>
        {dialogs}
      </>
    ) : (
      <section className="gt-card gt-partners is-sticky">
        {notice}
        <div className="gt-partner-grid is-list">
          <article className="gt-partner-card is-selected">
            <div className="gt-partner-card__logo"><Logo agent={stickyAgent} /></div>
            <div className="gt-partner-card__info">
              <h5>{partnerName(stickyAgent)}</h5>
              <p>{partnerAddress(stickyAgent)}</p>
              <button type="button" className="gt-link" onClick={() => openDetails(stickyAgent)}>Partner Details</button>
            </div>
            <Button className="gt-select is-selected" type="primary" tabIndex={-1}>Selected</Button>
          </article>
        </div>
        {dialogs}
      </section>
    );
  }

  if (isMobile) {
    return (
      <>
        <h2 className="gt-m-title">Please choose your preferred partner</h2>
        <Input
          size="large"
          className="gt-search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search city, area"
          prefix={<Icon path={mdiMagnify} size="20px" color="#747581" />}
          allowClear
        />
        <div className={`gt-m-chips ${chipsOpen ? "is-open" : ""}`}>
          <div className="gt-m-chips__list">
            {locationCounts.map(([location, count]) => (
              <button type="button" key={location} className={locations.includes(location) ? "is-active" : ""} onClick={() => toggleLocation(location)}>
                {location} ({count})
              </button>
            ))}
          </div>
          <button type="button" className="gt-m-chips__toggle" onClick={() => setChipsOpen((open) => !open)} aria-label={chipsOpen ? "Collapse locations" : "Expand locations"}>
            <Icon path={chipsOpen ? mdiChevronUp : mdiChevronDown} size="24px" />
          </button>
        </div>
        {notice}
        {loading ? (
          <div className="gt-center"><Spin /></div>
        ) : agents.length === 0 && !selected ? empty : (
          <div className="gt-m-partners">
            {visible.map((agent) => {
              const isSelected = sameAgent(agent, selected);
              return (
                <div key={agent.id} className={`gt-m-partner ${isSelected ? "is-selected" : ""}`}>
                  <button type="button" className="gt-m-partner__main" onClick={() => requestSelect(agent)} aria-pressed={isSelected}>
                    {isSelected ? <Icon path={mdiCheckCircle} size="28px" color="#dc1a22" /> : <i className="gt-m-partner__radio" />}
                    <span className="gt-m-partner__logo"><Logo agent={agent} /></span>
                    <span className="gt-m-partner__text">
                      <strong>{partnerName(agent)}</strong>
                      <small>{partnerAddress(agent)}</small>
                    </span>
                  </button>
                  <button type="button" className="gt-m-partner__info" onClick={() => openDetails(agent)} aria-label="Partner details">
                    <Icon path={mdiInformationOutline} size="26px" color="#1890ff" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {dialogs}
      </>
    );
  }

  return (
    <section className="gt-card gt-partners">
      {notice}

      <Input
        size="large"
        className="gt-search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search partners, locations, or services..."
        prefix={<Icon path={mdiMagnify} size="20px" color="#747581" />}
        allowClear
      />

      {loading ? (
        <div className="gt-center"><Spin /></div>
      ) : agents.length === 0 && !selected ? empty : (
        <div className="gt-partners__body">
          <aside className="gt-filters">
            <h4>Filters</h4>
            <h5>Location</h5>
            <div className="gt-filters__list">
              {locationCounts.map(([location, count]) => (
                <Checkbox key={location} checked={locations.includes(location)} onChange={() => toggleLocation(location)}>
                  {location} ({count})
                </Checkbox>
              ))}
            </div>
          </aside>

          <div className="gt-partners__results">
            <div className="gt-partners__toolbar">
              <span>{visible.length} partner{visible.length === 1 ? "" : "s"} found</span>
              <div className="gt-view-toggle" role="group" aria-label="View">
                <button type="button" className={view === "grid" ? "is-active" : ""} onClick={() => setView("grid")} aria-label="Grid view"><Icon path={mdiViewGridOutline} size="20px" /></button>
                <button type="button" className={view === "list" ? "is-active" : ""} onClick={() => setView("list")} aria-label="List view"><Icon path={mdiViewListOutline} size="20px" /></button>
              </div>
            </div>

            <div className={`gt-partner-grid is-${view}`}>
              {visible.map((agent) => {
                const isSelected = sameAgent(agent, selected);
                return (
                  <article key={agent.id} className={`gt-partner-card ${isSelected ? "is-selected" : ""}`}>
                    <div className="gt-partner-card__logo"><Logo agent={agent} /></div>
                    <div className="gt-partner-card__info">
                      <h5>{partnerName(agent)}</h5>
                      <p>{partnerAddress(agent)}</p>
                      <button type="button" className="gt-link" onClick={() => openDetails(agent)}>Partner Details</button>
                    </div>
                    <Button className={isSelected ? "gt-select is-selected" : "gt-select"} type={isSelected ? "primary" : "default"} onClick={() => requestSelect(agent)}>
                      {isSelected ? "Selected" : "Select"}
                    </Button>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {dialogs}
    </section>
  );
}
